import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { update } from "../../../../../api/onboarding";
import { GeaoContext } from "../../../GeaoContextProvider";
import { PageStatus } from "../../../hooks/useInterviewUpdater";

import { ApiRequestStatus } from "../../../constants/ApiRequestStatus";
import { GeaoPaths } from "../../../constants/GeaoPaths";
import { principalFormDataToQuestionAnswers } from "./mappers";

/**
 * Custom React hook for handling form events in the Principal flow Personal Information page.
 * Manages form submission, save and exit functionality, and API request states.
 * 
 * @param {Function} [getValues=() => ({})] - Function to retrieve current form values
 * @param {Function} [trigger=() => Promise.resolve()] - Function to trigger form validation
 * @returns {Object} Object containing form event handlers and loading state
 * @returns {Function} returns.onSubmit - Handler for form submission that navigates to next page
 * @returns {Function} returns.onSaveAndExit - Handler for saving progress and returning to checklist
 * @returns {boolean} returns.saving - Boolean indicating if an API request is in progress
 */
export default function useFormEvents(
    getValues = () => ({}),
    trigger = () => { return Promise.resolve(); }
) {
    const navigate = useNavigate();

    const { agentInterview, setAgentInterview } = useContext(GeaoContext);
    const [updateStatus, setUpdateStatus] = useState(ApiRequestStatus.NotSent);
    const [nextPath, setNextPath] = useState(GeaoPaths.CreateYourProfileChecklist);

    const saving = updateStatus === ApiRequestStatus.Pending;

    /**
     * Effect hook that handles navigation after successful API updates.
     * Navigates to kickout page if agent is rejected, otherwise navigates to the next path.
     */
    useEffect(() => {
        if (updateStatus != ApiRequestStatus.Success) return;

        if (agentInterview?.assessments?.NotACaptiveAgent?.status === "Rejected") {
            navigate(GeaoPaths.CreateYourProfileKickout);
            return;
        }

        navigate(nextPath);
    }, [updateStatus, nextPath, agentInterview]);

    /**
     * Handles form submission and proceeds to the next step in the principal flow.
     * Sets the next path to Principal Background Information and updates the interview.
     * 
     * @param {Object} formData - The form data to be submitted
     */
    const onSubmit = (formData) => {
        setNextPath(GeaoPaths.PrincipalBackgroundInformation);
        updateInterview(formData, false);
    };

    /**
     * Handles the save and exit action.
     * Triggers form validation, then saves current form values and navigates back to checklist.
     */
    const onSaveAndExit = () => {
        trigger().then(_ => {
            setNextPath(GeaoPaths.CreateYourProfileChecklist);
            updateInterview(getValues(), true);
        });
    };

    /**
     * Updates the agent interview with form data via API call.
     * Manages API request state and handles success/error scenarios.
     * 
     * @param {Object} formData - The form data to be saved
     * @param {boolean} isSaveAndExit - Whether this is a save and exit operation
     */
    const updateInterview = (formData, isSaveAndExit) => {
        if (updateStatus == ApiRequestStatus.Pending) return;

        // Create activity request for save and exit operations
        const activityRequest = isSaveAndExit ? {
            activityCategory: "GEAO_ONBOARDING",
            activitySubType: "SAVE_AND_EXIT",
            sourceApplication: "AGIWUI",
            additionalData: {
                componentName: "PrincipalPersonalInformation"
            }
        } : null;

        // Prepare the update request payload
        const updateAgentInterviewRequest = {
            activityRequest,
            identityType: "Agent",
            interview: {
                ...agentInterview,
                questionAnswers: {
                    ...agentInterview.questionAnswers,
                    ...principalFormDataToQuestionAnswers(formData)
                },
                pageStatus: {
                    ...agentInterview.pageStatus,
                    "PrincipalPersonalInformation": PageStatus.Completed
                }
            }
        };

        setUpdateStatus(ApiRequestStatus.Pending);

        update(updateAgentInterviewRequest,
            interview => {
                // Success
                setAgentInterview(interview);
                setUpdateStatus(ApiRequestStatus.Success);
            },
            () => {
                // Finally
            },
            _ => {
                // Error
                setUpdateStatus(ApiRequestStatus.Error);
            });
    };

    return {
        onSubmit,
        onSaveAndExit,
        saving
    };
}